import { useEffect, useState } from "react";

import { I } from "../../components/icons";
import { DiscloseRow, NavRow, RowGroup } from "../../components/Rows";
import { dayLabel } from "../../domain/date";
import { DEFAULT_SOURCES, FOOD_CAVEAT, sourcesNotice } from "../../domain/sources";
import { loadFoods, shippedSources } from "../../lib/foodDb";
import SettingsPage from "./SettingsPage";
import "./AboutSettings.css";

/** What ships in the bundle, each licence read from the package or the font's own OFL file. */
const LICENCES: readonly { name: string; licence: string }[] = [
  { name: "Ionic Framework", licence: "MIT" },
  { name: "React", licence: "MIT" },
  { name: "React Router", licence: "MIT" },
  { name: "Zustand", licence: "MIT" },
  { name: "Immer", licence: "MIT" },
  { name: "Fuse.js", licence: "Apache-2.0" },
  { name: "idb-keyval", licence: "Apache-2.0" },
  { name: "Workbox", licence: "MIT" },
  { name: "Manrope, Barlow Semi Condensed and Noto Sans Devanagari", licence: "SIL Open Font License 1.1" },
];

/**
 * About Kaya: the version, the sources and licences, and where the data lives.
 *
 * The one place a food source is named (ADR-033 §8). The notice is built from the tables the app
 * ships — the one it opens with, and the wider one a search can fetch (lib/foodDb.ts) — so a source
 * dropped from the build is no longer credited; until the table arrives, or if it never does
 * offline on a first launch, it states the full set rather than nothing.
 */
export default function AboutSettings() {
  const [present, setPresent] = useState<ReadonlySet<string>>(DEFAULT_SOURCES);

  useEffect(() => {
    let live = true;
    loadFoods()
      .then(() => {
        const seen = shippedSources();
        if (live && seen.size) setPresent(seen);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const notice = sourcesNotice(present);

  return (
    <SettingsPage title="About Kaya">
      <RowGroup heading="This app">
        <NavRow
          kind="value"
          icon={I.info}
          title={`Version ${__APP_VERSION__}`}
          subtitle={`Built ${dayLabel(__BUILD_DATE__)} ${__BUILD_DATE__.slice(0, 4)} · build ${__BUILD_COMMIT__}`}
        />
      </RowGroup>

      <RowGroup heading="Sources and licences">
        <DiscloseRow icon={I.book} title="Where the food values come from" defaultOpen>
          <div className="you-about-notice">
            {notice.map((part, i) => (
              <p key={part.src.join("")}>
                {i === 0 ? <strong>Food values.</strong> : null}
                {i === 0 ? " " : null}
                {part.text}
              </p>
            ))}
            <p className="you-about-caveat">{FOOD_CAVEAT}</p>
          </div>
        </DiscloseRow>
        <DiscloseRow
          icon={I.list}
          title="Open-source licences"
          subtitle="The libraries and fonts Kaya is built with"
        >
          <ul className="you-about-list">
            {LICENCES.map((l) => (
              <li key={l.name}>
                {l.name} ({l.licence})
              </li>
            ))}
          </ul>
        </DiscloseRow>
      </RowGroup>

      <RowGroup heading="Your data">
        <NavRow
          kind="value"
          icon={I.lock}
          title="Privacy"
          subtitle="Your data stays on your phone and your own server"
        />
      </RowGroup>
    </SettingsPage>
  );
}
